import axios from 'axios';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { listTaxes, updateTax } from 'services/additionalTax';
import { editConfig } from 'services/config';
import { AdditionalTax } from 'shared/models/additionalTax';
import { IAdditionalTax } from 'shared/types/additionalTax';
import { useAppTabContext } from '../../AppContext';
import { getFeesFromDriver } from '../IntegratedPayment/helpers/feesMapping';
import AdditionalTaxComponent from './AdditionalTax';

export const YOOGA_ADDITIONAL_TAX_CONFIG =
  'feature_toggle_yooga_additional_tax';

interface AdditionalTaxContextData {
  taxes: AdditionalTax[];
  loading: boolean;
  isRegistered: boolean;
  isClubEnabled: boolean;
  setLoading(b: boolean): void;
  handleSubmit(isEnabled: boolean): void;
  handleDisableClub(): void;
  handleChangeValue(value: AdditionalTax): void;
  currentValue: AdditionalTax | undefined;
}

const Context = createContext<AdditionalTaxContextData>(
  {} as AdditionalTaxContextData,
);

export const useAdditionalTaxContext = () => useContext(Context);

const AdditionalTaxContext = () => {
  const { app, rawConfigs, getDriver, updateConfig } = useAppTabContext();
  const { customer } = useCustomerContext();

  const isClubEnabled =
    rawConfigs?.find((config) => config.name === YOOGA_ADDITIONAL_TAX_CONFIG)
      ?.booleanValue === true;

  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(true);
  const [taxes, setTaxes] = useState<AdditionalTax[]>([]);
  const [currentValue, setCurrentValue] = useState<AdditionalTax | undefined>(
    {} as AdditionalTax,
  );

  const handleChangeValue = (value: AdditionalTax) => {
    setCurrentValue(value);
  };

  const saveClubConfig = (value: boolean) =>
    editConfig(String(app.idi), {
      name: YOOGA_ADDITIONAL_TAX_CONFIG,
      value,
      customerId: customer?.id,
      marketplaceFeesOld: getFeesFromDriver(getDriver),
    }).then((response) => {
      updateConfig(response.data.configs);
    });

  const handleSubmit = (isEnabled: boolean) => {
    if (!isEnabled) {
      toast.error('Necessário ativar o toogle!');
      return;
    }

    if (!currentValue) {
      console.error(
        'Falha ao atualizar taxa de serviço: currentValue is not defined',
      );
      toast.error('Falha ao atualizar taxa de serviço!');
      return;
    }

    setLoading(true);

    updateTax(app.idi, currentValue.id)
      .then(() => saveClubConfig(true))
      .then(() => {
        toast.success('Taxa de serviço atualizada com sucesso!');
      })
      .catch((e) => {
        console.error('Falha ao atualizar taxa de serviço', e.message);
        toast.error('Falha ao atualizar taxa de serviço!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDisableClub = () => {
    setLoading(true);

    saveClubConfig(false)
      .then(() => {
        toast.success('Taxa de serviço desativada com sucesso!');
      })
      .catch((e) => {
        console.error('Falha ao desativar taxa de serviço', e.message);
        toast.error('Falha ao desativar taxa de serviço!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    listTaxes(String(app.idi))
      .then((res) => {
        const taxesData: AdditionalTax[] = res.data.map(
          (tax: IAdditionalTax) => new AdditionalTax(tax),
        );
        setTaxes(taxesData);

        if (taxesData.length) {
          const activeTax = taxesData.find((tax) => {
            return tax.enabled === true;
          });
          setCurrentValue(activeTax);
        }
      })
      .catch((error) => {
        if (
          axios.isAxiosError(error) &&
          error.response?.status !== 404 &&
          error.response?.status !== 403
        ) {
          console.error('Falha ao carregar as taxas', error);
          toast.error('Falha ao carregar as taxas de serviço');
        }

        if (error?.response?.status === 422) {
          setIsRegistered(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [app.idi]);

  const data: AdditionalTaxContextData = {
    taxes,
    loading,
    isRegistered,
    isClubEnabled,
    setLoading,
    handleSubmit,
    handleDisableClub,
    handleChangeValue,
    currentValue,
  };

  return (
    <Context.Provider value={data}>
      <AdditionalTaxComponent />
    </Context.Provider>
  );
};

export default AdditionalTaxContext;
